// src/components/quotes/quoteTerms.js
//
// TERMS & CONDITIONS ON A QUOTE.
//
// ⛔ THE ARCHITECTURAL DECISION, AND IT IS NOT THE OBVIOUS ONE:
// SETTINGS HOLDS THE DEFAULT. EACH QUOTE HOLDS ITS OWN COPY.
//
// The tempting build is the one the proposals already use — a module-level
// `const TERMS = [...]` in BronzeTemplate.jsx, duplicated in Silver and Gold.
// That pattern is deliberately NOT copied here. It has two faults:
//
//   1. changing a commercial term needs a code deploy;
//   2. it REWRITES HISTORY. Every proposal ever rendered picks up *today's*
//      TERMS, so reopening a document from last year shows terms that did not
//      exist when it was sent.
//
// For a quote the second fault is not cosmetic. Acceptance is about to become
// financially operative (accept -> QBO invoice -> Autotask ticket), and the
// terms attached to an accepted offer are WHAT THE CUSTOMER AGREED TO. If those
// terms were a live global, editing the default would silently alter the
// agreement behind every quote already accepted under the old wording.
//
// So the quote is the record of what was offered:
//
//     Settings holds THE DEFAULT      <- Craig edits it here, no deploy
//     Each quote holds ITS OWN COPY   <- seeded at creation, never mutated after
//
// ⚠ Editing the Settings default therefore changes NEW quotes only. That is the
// whole point, and it is the thing that will read as a bug the first time an
// open draft does not change — which is why the Settings panel says so.
//
// Kept React-free so it is unit-testable and so the record factory, the
// migration and the builder can all reach it.

/* ══════════════════════════════════════════════════════════════════════════
   THE SEED

   ⚠ THIS IS A SEED, NOT THE SOURCE OF TRUTH. It is the value Settings starts
   at; once Craig has edited it, Settings wins and this constant is never read
   again for a new quote. A payment term must be changeable without a deploy.

   ✅ APPROVED WORDING — Craig authorised both corrections 2026-09-06. Two
   things were changed from his original, both his own commercial wording, so
   they were flagged rather than silently corrected. Recorded here so a future
   reader does not "fix" them back:

       ¶2  "Upon receiving payment ADVISE"   -> "payment ADVICE"
       ¶3  "This PROPOSAL assumes…"          -> "This QUOTE assumes…"

   "Proposal" had been carried over from proposal boilerplate; on a document
   headed QUOTATION it read as a copy-paste slip.

   ⚠ THE EFFECTIVE DATE IS DELIBERATELY UNCHANGED. "as of the 1st of April
   2020" stays exactly as written. It is six years old and worth Craig's eye at
   some point, but stating when a policy took effect is normal and correct — an
   effective date on a payment term is a COMMERCIAL FACT, not a typo to tidy. It
   was not part of what Craig approved. Do not touch it without him saying so in
   as many words.

   ⚠ ONE CLAUSE OF THREE PARAGRAPHS, not three clauses. It is a single
   payment-terms statement and reads as one.
   ══════════════════════════════════════════════════════════════════════════ */
export const DEFAULT_QUOTE_TERMS_TEXT = [
  "Please be aware, as of the 1st of April 2020, all accepted quotes will need to be paid in full prior to delivery of any hardware and software.",
  "Upon acceptance of quote Datatech will raise an invoice and send this through to you for payment. Upon receiving payment advice, Datatech will deliver goods as specified in the accepted quote.",
  "This quote assumes all existing hardware and software meets the requirement to integrate with our solution.",
].join("\n\n");

let seq = 0;
/* ⚠ Not `Date.now()` alone. Seeding a quote and adding a clause in the same
   millisecond would mint one id twice, and React would then render one of them
   into the other's box. Same collision the quote id itself hit in Stage D. */
function clauseId() {
  seq += 1;
  return `tc-${Date.now()}-${seq}`;
}

/**
 * A clause.
 *
 * `required` is about DELETABILITY, not editability. Craig: "I want the first
 * T&C to always be in a quote." It stays editable, because occasionally a client
 * negotiates a term and the quote has to reflect what was actually agreed —
 * with a visible restore-default beside it.
 */
export function makeClause(text = "", { required = false } = {}) {
  return { id: clauseId(), text: String(text), required };
}

/**
 * The terms a NEW quote starts with — one required clause, holding whatever
 * Settings currently says.
 *
 * ⚠ Falls back to the seed when Settings holds nothing usable. A quote with NO
 * terms is the worse outcome: the section simply would not print, and nobody
 * would notice until a customer asked what the payment terms were.
 */
export function seedQuoteTerms(defaultText) {
  const text = typeof defaultText === "string" && defaultText.trim()
    ? defaultText
    : DEFAULT_QUOTE_TERMS_TEXT;
  return [makeClause(text, { required: true })];
}

/**
 * The terms for a quote that may predate the feature.
 *
 * ⚠ RETURNS THE SAME ARRAY when there is nothing to do. The migration runs on
 * every read of a quote, so returning a fresh array each time would give React
 * a new reference on every render and make the debounced save fire forever.
 */
export function withQuoteTerms(quote, defaultText) {
  if (!quote) return quote;
  if (Array.isArray(quote.terms) && quote.terms.length) return quote;
  return { ...quote, terms: seedQuoteTerms(defaultText) };
}

/* ── Editing ─────────────────────────────────────────────────────────────────
   All pure, all returning a new array, so the builder can hand the result
   straight to its one updater. */

export function addClause(terms, text = "") {
  return [...(terms || []), makeClause(text)];
}

export function updateClause(terms, id, text) {
  return (terms || []).map((c) => (c.id === id ? { ...c, text: String(text) } : c));
}

/**
 * ⛔ A REQUIRED CLAUSE CANNOT BE DELETED, and the guard lives HERE rather than
 * only in the UI. A disabled button is a suggestion; this is the rule. Anything
 * that reaches the array through another path — a keyboard shortcut, a future
 * bulk edit, a paste — still cannot drop it.
 */
export function removeClause(terms, id) {
  return (terms || []).filter((c) => !(c.id === id && !c.required));
}

/**
 * Move a clause one place.
 *
 * ⚠ THE REQUIRED CLAUSE IS PINNED FIRST and neither moves nor is displaced.
 * "The first T&C is always in a quote" is a statement about position as much as
 * presence — a payment term that has drifted to the bottom of a list of six is
 * not what Craig described, and reordering is only meaningful for the clauses
 * he adds himself.
 */
export function moveClause(terms, id, delta) {
  const list = [...(terms || [])];
  const from = list.findIndex((c) => c.id === id);
  if (from < 0) return list;
  if (list[from].required) return list;
  const firstMovable = list.findIndex((c) => !c.required);
  const to = from + delta;
  if (to < Math.max(0, firstMovable) || to >= list.length) return list;
  if (list[to] && list[to].required) return list;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list;
}

/** Put the Settings default back into the required clause. */
export function restoreDefaultClause(terms, defaultText) {
  const text = typeof defaultText === "string" && defaultText.trim()
    ? defaultText
    : DEFAULT_QUOTE_TERMS_TEXT;
  const list = terms || [];
  if (!list.some((c) => c.required)) return seedQuoteTerms(defaultText).concat(list);
  return list.map((c) => (c.required ? { ...c, text } : c));
}

/**
 * The clauses that actually print.
 *
 * ⚠ AN EMPTY CLAUSE IS DROPPED, not printed as a numbered blank. Craig can
 * clear a box while thinking; a document that prints "3." with nothing after it
 * reads as a fault in the quote.
 */
export function printableClauses(terms) {
  return (terms || [])
    .map((c) => (typeof c === "string" ? { text: c } : c))
    .filter((c) => c && typeof c.text === "string" && c.text.trim());
}
